import { Box, Typography } from "@mui/material";
import { useEffect, useRef, type JSX } from "react";

/**
 * A <video> that binds its MediaStream via its OWN ref/effect, so it renders the
 * stream no matter WHEN it mounts relative to the stream arriving. The
 * CallWindow-owned `remoteScreenRef` + bind effect had a mount-vs-bind race: in a
 * group call the screen <video> only mounts when the layout switches to the
 * screen stage, which can happen AFTER the stream is already set — the effect
 * (keyed on the stream, unchanged) never re-fires, so the element stays black.
 * Binding in the leaf's own mount effect closes that race. (Mirrors
 * ParticipantGrid's ParticipantVideo.)
 */
function BoundVideo({
  stream,
  testid,
  muted,
  objectFit,
}: {
  stream: MediaStream;
  testid: string;
  muted: boolean;
  objectFit: "cover" | "contain";
}): JSX.Element {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || el.srcObject === stream) return;
    // Tolerate environments (happy-dom / WKWebView) that reject srcObject or
    // don't autoplay from the attribute — same guard as CallWindow.setSrcObject.
    try {
      el.srcObject = stream;
      const p = el.play?.();
      if (p && typeof p.catch === "function") p.catch(() => { /* autoplay/not-ready */ });
    } catch {
      /* unsupported env / invalid stream — ignore */
    }
  }, [stream]);
  return (
    <video
      ref={ref}
      data-testid={testid}
      autoPlay
      playsInline
      muted={muted}
      style={{ width: "100%", height: "100%", objectFit, display: "block" }}
    />
  );
}

/**
 * The GROUP-call screen-share stage. `ParticipantGrid` renders camera tiles only,
 * so when someone shared their screen in a 3+ party call the stream arrived but
 * had nowhere to render → every viewer saw black. This shows the screen as the
 * primary surface (1:1's `VideoStage` already did this; brings the group to
 * parity), with the viewer's own camera as a PiP. The screen is the focus while
 * presenting, so the camera grid steps aside (audio meters + the header still
 * convey who's on the call).
 */
export function ScreenShareStage({
  screenStream,
  ownScreen,
  localCameraStream,
  maximized,
  pipW,
  pipH,
}: {
  /** The screen MediaStream to show big — the remote sharer's, or our own. */
  screenStream: MediaStream;
  /** True when this is OUR screen (mute the preview + show the "sharing" badge). */
  ownScreen: boolean;
  /** Our own camera, shown as a PiP when on. */
  localCameraStream: MediaStream | null;
  maximized: boolean;
  pipW: number;
  pipH: number;
}): JSX.Element {
  return (
    <Box
      data-testid="call-screen-stage"
      sx={{
        position: "relative",
        width: "100%",
        bgcolor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(maximized ? { flex: 1, minHeight: 0 } : { aspectRatio: "16 / 9" }),
      }}
    >
      <BoundVideo stream={screenStream} testid="group-screen-video" muted={ownScreen} objectFit="contain" />

      {ownScreen && (
        <Typography
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            color: "rgba(255,255,255,0.85)",
            fontSize: 11,
            fontWeight: 600,
            bgcolor: "rgba(0,0,0,0.55)",
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
          }}
        >
          You're sharing
        </Typography>
      )}

      {localCameraStream && (
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            width: pipW,
            height: pipH,
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.35)",
          }}
        >
          <BoundVideo stream={localCameraStream} testid="group-self-camera-video" muted objectFit="cover" />
        </Box>
      )}
    </Box>
  );
}
